import { createFileRoute, Link } from '@tanstack/react-router';
import { formatInTimeZone } from 'date-fns-tz';
import {
    ArrowLeft,
    ExternalLink,
    Lightbulb,
    MessageCircle,
} from 'lucide-react';
import { Fragment, useState } from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
} from '@apps/web/components/ui/card';
import { Spinner } from '@apps/web/components/ui/spinner';
import { Tabs, TabsList, TabsTrigger } from '@apps/web/components/ui/tabs';
import { useAdminImessageRequestCatalog } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type AdminImessageRequestCatalogItemRead,
    type AdminImessageRequestExampleRead,
} from '@libraries/typescript/api_client';

export const Route = createFileRoute('/_app/admin/imessage-requests')({
    head: () => ({
        meta: [
            {
                title: 'iMessage Requests | Phoebe Admin',
            },
        ],
    }),
    component: ImessageRequestsPage,
});

type CatalogView = 'unmet' | 'all';

const REQUEST_OUTCOME_LABELS: Record<
    AdminImessageRequestExampleRead['outcome'],
    string
> = {
    fulfilled: 'Fulfilled',
    partially_fulfilled: 'Partially fulfilled',
    not_fulfilled: 'Not fulfilled',
    unclear: 'Unclear',
};

function unmetCount(item: AdminImessageRequestCatalogItemRead): number {
    return item.not_fulfilled_count + item.partially_fulfilled_count;
}

export function ImessageRequestsPage() {
    const { data, error, isLoading } = useAdminImessageRequestCatalog();
    const [view, setView] = useState<CatalogView>('unmet');
    const unmetItems = [...(data?.items ?? [])]
        .filter((item) => unmetCount(item) > 0)
        .sort(
            (left, right) =>
                unmetCount(right) - unmetCount(left) ||
                right.conversation_count - left.conversation_count,
        );
    const displayedItems = view === 'unmet' ? unmetItems : (data?.items ?? []);
    const totalUnmet = (data?.items ?? []).reduce(
        (total, item) => total + unmetCount(item),
        0,
    );

    return (
        <div className="flex h-full min-w-0 flex-col overflow-hidden">
            <div className="border-border min-w-0 border-b px-4 py-4 sm:px-6">
                <div className="mx-auto flex w-full max-w-7xl flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <Lightbulb className="h-5 w-5 text-amber-500" />
                            <h1 className="text-xl font-semibold">
                                iMessage requests
                            </h1>
                        </div>
                        <p className="text-muted-foreground mt-1 max-w-3xl text-sm">
                            Common things customer staff ask Phoebe to do,
                            clustered from production conversations. Test, demo,
                            sandbox, and internal accounts are excluded.
                        </p>
                    </div>
                    <Button asChild={true} variant="outline" size="sm">
                        <Link to="/admin/imessage">
                            <ArrowLeft className="h-4 w-4" />
                            Conversations
                        </Link>
                    </Button>
                </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
                <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-4 sm:p-6">
                    {isLoading ? (
                        <LoadingState />
                    ) : error || !data ? (
                        <ErrorState />
                    ) : (
                        <Fragment>
                            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                                <SummaryCard
                                    label="Conversations analyzed"
                                    value={data.analyzed_conversation_count}
                                />
                                <SummaryCard
                                    label="Requests found"
                                    value={data.request_observation_count}
                                />
                                <SummaryCard
                                    label="Unmet or partial"
                                    value={totalUnmet}
                                />
                                <SummaryCard
                                    label="Customer accounts"
                                    value={data.customer_organization_count}
                                />
                            </div>
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                                <div>
                                    <h2 className="font-semibold">
                                        Feature catalog
                                    </h2>
                                    <p className="text-muted-foreground text-sm">
                                        {data.last_analyzed_at
                                            ? `Last refreshed ${formatInTimeZone(
                                                  new Date(
                                                      data.last_analyzed_at,
                                                  ),
                                                  'UTC',
                                                  "MMM d, yyyy 'at' h:mm a 'UTC'",
                                              )} with ${data.model}`
                                            : `Waiting for the first ${data.model} batch`}
                                    </p>
                                </div>
                                <Tabs
                                    value={view}
                                    onValueChange={(value) => {
                                        if (
                                            value === 'unmet' ||
                                            value === 'all'
                                        ) {
                                            setView(value);
                                        }
                                    }}
                                >
                                    <TabsList>
                                        <TabsTrigger value="unmet">
                                            Unmet requests ({unmetItems.length})
                                        </TabsTrigger>
                                        <TabsTrigger value="all">
                                            All requests ({data.items.length})
                                        </TabsTrigger>
                                    </TabsList>
                                </Tabs>
                            </div>
                            {displayedItems.length === 0 ? (
                                <EmptyState
                                    hasAnalyses={
                                        data.analyzed_conversation_count > 0
                                    }
                                    view={view}
                                />
                            ) : (
                                <div className="space-y-4">
                                    {displayedItems.map((item, index) => (
                                        <RequestCard
                                            key={item.feature_key}
                                            item={item}
                                            rank={index + 1}
                                            unmetOnly={view === 'unmet'}
                                        />
                                    ))}
                                </div>
                            )}
                        </Fragment>
                    )}
                </div>
            </div>
        </div>
    );
}

function SummaryCard({
    label,
    value,
}: {
    readonly label: string;
    readonly value: number;
}) {
    return (
        <Card className="gap-2 py-4">
            <CardContent className="px-4">
                <div className="text-2xl font-semibold tabular-nums">
                    {value.toLocaleString()}
                </div>
                <div className="text-muted-foreground text-sm">{label}</div>
            </CardContent>
        </Card>
    );
}

function RequestCard({
    item,
    rank,
    unmetOnly,
}: {
    readonly item: AdminImessageRequestCatalogItemRead;
    readonly rank: number;
    readonly unmetOnly: boolean;
}) {
    const unmet = unmetCount(item);
    const examples = unmetOnly
        ? item.examples.filter(
              (example) =>
                  example.outcome === 'not_fulfilled' ||
                  example.outcome === 'partially_fulfilled',
          )
        : item.examples;
    return (
        <Card className="gap-4 py-5">
            <CardHeader className="gap-3 px-5 sm:grid-cols-[auto_1fr_auto]">
                <div className="bg-muted text-muted-foreground flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold tabular-nums">
                    {rank}
                </div>
                <div className="min-w-0">
                    <h3 className="leading-6 font-semibold">{item.title}</h3>
                    <CardDescription className="mt-1">
                        {item.summary}
                    </CardDescription>
                </div>
                <div className="flex flex-wrap gap-1.5 sm:justify-end">
                    {unmet > 0 ? (
                        <Badge variant="destructive">
                            {unmet} unmet or partial
                        </Badge>
                    ) : (
                        <Badge variant="success">Fulfilled only</Badge>
                    )}
                    <Badge variant="outline">
                        {item.conversation_count} conversations
                    </Badge>
                    <Badge variant="outline">
                        {item.organization_count} accounts
                    </Badge>
                </div>
            </CardHeader>
            <CardContent className="space-y-4 px-5">
                <div className="text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 text-xs">
                    <span>{item.fulfilled_count} fulfilled</span>
                    <span>
                        {item.partially_fulfilled_count} partially fulfilled
                    </span>
                    <span>{item.not_fulfilled_count} not fulfilled</span>
                    <span>{item.unclear_count} unclear</span>
                </div>
                {examples.length > 0 ? (
                    <div className="space-y-2">
                        <h4 className="text-sm font-medium">Recent examples</h4>
                        {examples.map((example) => (
                            <RequestExample
                                key={`${example.thread_id}-${example.requested_at}`}
                                example={example}
                            />
                        ))}
                    </div>
                ) : null}
            </CardContent>
        </Card>
    );
}

function RequestExample({
    example,
}: {
    readonly example: AdminImessageRequestExampleRead;
}) {
    return (
        <div className="bg-muted/40 rounded-sm border p-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline">
                            {REQUEST_OUTCOME_LABELS[example.outcome]}
                        </Badge>
                        <p className="text-sm">{example.example_request}</p>
                    </div>
                    {example.limitation_reason ? (
                        <p className="text-muted-foreground mt-1 text-xs">
                            {example.limitation_reason}
                        </p>
                    ) : null}
                    <p className="text-muted-foreground mt-1 text-xs">
                        {example.organization_name} ·{' '}
                        {formatInTimeZone(
                            new Date(example.requested_at),
                            example.organization_timezone,
                            'MMM d, yyyy',
                        )}
                    </p>
                </div>
                <Button asChild={true} variant="ghost" size="sm">
                    <Link
                        to="/admin/imessage"
                        search={{ threadId: example.thread_id }}
                    >
                        Open conversation
                        <ExternalLink className="h-3.5 w-3.5" />
                    </Link>
                </Button>
            </div>
        </div>
    );
}

function LoadingState() {
    return (
        <div className="text-muted-foreground flex h-48 items-center justify-center gap-2 text-sm">
            <Spinner size="sm" />
            <span>Loading request catalog…</span>
        </div>
    );
}

function ErrorState() {
    return (
        <Card>
            <CardContent className="text-destructive flex h-32 items-center justify-center text-sm">
                Failed to load the iMessage request catalog.
            </CardContent>
        </Card>
    );
}

function EmptyState({
    hasAnalyses,
    view,
}: {
    readonly hasAnalyses: boolean;
    readonly view: CatalogView;
}) {
    return (
        <Card>
            <CardContent className="text-muted-foreground flex min-h-48 flex-col items-center justify-center gap-2 text-center">
                <MessageCircle className="h-7 w-7" />
                <p className="text-foreground font-medium">
                    {view === 'unmet' && hasAnalyses
                        ? 'No unmet requests found'
                        : 'No request analyses yet'}
                </p>
                <p className="max-w-md text-sm">
                    {view === 'unmet' && hasAnalyses
                        ? 'The analyzed conversations only contain fulfilled requests. Switch to all requests to review them.'
                        : 'The production batch agent will populate this page after it analyzes customer iMessage conversations.'}
                </p>
            </CardContent>
        </Card>
    );
}
